import { type FC, type HTMLAttributes, useCallback } from "react";
import { useFormContext } from "react-hook-form";

export type ToggleUIProps = {
  registerName: `${string}` | `${string}.${string}` | `${string}.${number}`;
  label?: string;
  toggleSize?: "xs" | "sm" | "md" | "lg";
  color?:
    | "primary"
    | "secondary"
    | "accent"
    | "ghost"
    | "link"
    | "info"
    | "success"
    | "warning"
    | "error"
    | "default";
} & HTMLAttributes<HTMLDivElement>;

export const ToggleUI: FC<ToggleUIProps> = ({
  registerName,
  color = "default",
  toggleSize = "sm",
  label,
  className,
  ...rest
}) => {
  const { register } = useFormContext();
  const getColor = useCallback(
    () =>
      ({
        primary: "toggle-primary",
        secondary: "toggle-secondary",
        accent: "toggle-accent",
        ghost: "toggle-ghost",
        link: "toggle-link",
        info: "toggle-info",
        success: "toggle-success",
        warning: "toggle-warning",
        error: "toggle-error",
        default: "",
      })[color],
    [color],
  );

  const getSize = useCallback(
    () =>
      ({
        xs: "toggle-xs",
        sm: "toggle-sm",
        md: "toggle-md",
        lg: "toggle-lg",
      })[toggleSize],
    [toggleSize],
  );

  const containerClass = `form-control ${
    label ? "w-52" : "w-auto flex justify-start items-center"
  }`;
  const labelClass = `cursor-pointer label ${label ? "" : "justify-start"}`;

  return (
    <div className={`${containerClass} ${className}`} {...rest}>
      <label className={labelClass}>
        {label && <span className="label-text">{label}</span>}
        <input
          type="checkbox"
          className={`toggle ${getColor()} ${getSize()} ${label ? "" : "ml-0"}`}
          {...register(registerName)}
        />
      </label>
    </div>
  );
};
